import { isIngestionAction } from "@/shared/domain/actions";
import {
  firesOnFileArrival,
  type IngestionRuleRecord,
  pathPatternMatches,
  rulePrefillFromTrigger,
  samplePathsForPatterns,
} from "@/shared/domain/ingestionRules";
import {
  isManaged,
  TriggerTargetType,
  type InvokeActionTarget,
  type TriggerRecord,
} from "@/shared/domain/triggers";

function matchesAny(patterns: readonly string[], paths: readonly string[]) {
  return paths.some((path) =>
    patterns.some((pattern) => pathPatternMatches(pattern, path)),
  );
}

/** The file patterns a trigger target runs on; empty means every file. */
function targetPatterns(
  trigger: TriggerRecord,
  target: InvokeActionTarget,
): string[] {
  return rulePrefillFromTrigger(trigger, target).includePatterns;
}

/**
 * The enabled triggers people wrote that run an ingestion action on uploaded
 * files matching any of `includePatterns`, checked both ways round with
 * example paths from each side's patterns.
 */
export function overlappingIngestionTriggers(
  triggers: readonly TriggerRecord[],
  includePatterns: readonly string[],
): TriggerRecord[] {
  if (includePatterns.length === 0) {
    return [];
  }
  const rulePaths = samplePathsForPatterns(includePatterns);
  return triggers.filter(
    (trigger) =>
      trigger.enabled &&
      !isManaged(trigger) &&
      firesOnFileArrival(trigger.fires_on) &&
      trigger.targets.some((target) => {
        if (
          target.type !== TriggerTargetType.InvokeAction ||
          !isIngestionAction(target.action)
        ) {
          return false;
        }
        const patterns = targetPatterns(trigger, target);
        return (
          patterns.length === 0 ||
          matchesAny(patterns, rulePaths) ||
          matchesAny(includePatterns, samplePathsForPatterns(patterns))
        );
      }),
  );
}

/**
 * The rules with an example path, from their own patterns and not excluded by
 * them, that any of `includePatterns` matches. This finds a narrower rule that
 * the server's matcher misses when asked about the broader rule's example
 * paths: `logs/**\/*.mcap` matches none of `**\/*.mcap`'s.
 */
export function rulesWithFilesMatching(
  rules: readonly IngestionRuleRecord[],
  includePatterns: readonly string[],
): IngestionRuleRecord[] {
  return rules.filter((rule) =>
    matchesAny(
      includePatterns,
      samplePathsForPatterns(rule.include_patterns).filter(
        (path) => !matchesAny(rule.exclude_patterns, [path]),
      ),
    ),
  );
}
